/**
 * GitHub Copilot AI client using device code flow
 * Docs: https://docs.github.com/en/apps/oauth-apps/building-oauth-apps/authorizing-oauth-apps#device-flow
 */

import type {
  AIClient,
  ChatCompletionRequest,
  ChatCompletionResponse,
  DeviceCodeResponse,
  DeviceTokenResponse,
  GenerateStoryRequest,
  GenerateStoryResponse,
} from "@/lib/ai/types.ts";
import { PROVIDERS } from "@/lib/ai/config.ts";

const POLLING_INTERVAL = 5000; // 5 seconds default

export class CopilotClient implements AIClient {
  provider = "copilot" as const;
  accessToken: string;

  constructor(accessToken: string) {
    this.accessToken = accessToken;
  }

  /**
   * Step 1: Request device code
   */
  static async requestDeviceCode(): Promise<DeviceCodeResponse> {
    const response = await fetch(`/api/auth/copilot/start`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include", // Important for cookies
    });

    if (!response.ok) {
      throw new Error(`Device code request failed: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      device_code: data.device_code || "",
      user_code: data.user_code,
      verification_uri: data.verification_uri,
      expires_in: data.expires_in,
      interval: data.interval,
    };
  }

  /**
   * Step 2: Poll for access token
   * Returns null if still pending, throws on error
   */
  static async pollForToken(): Promise<DeviceTokenResponse | null> {
    const response = await fetch(`/api/auth/copilot/poll`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include", // Important for cookies
    });

    if (!response.ok) {
      throw new Error(`Token polling failed: ${response.statusText}`);
    }

    const data = await response.json();

    // Handle different states from backend
    if (data.status === "success") {
      // Backend successfully got the token
      return {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        token_type: "bearer",
      };
    }
    if (data.status === "pending") {
      return null; // Still waiting for user
    }
    if (data.status === "slow_down") {
      throw new Error("Polling too fast, slow down");
    }
    if (data.error) {
      throw new Error(`Auth error: ${data.error}`);
    }

    return null;
  }

  /**
   * Complete device code flow (polls until success)
   */
  static async completeDeviceFlow(
    onProgress?: (secondsElapsed: number) => void,
  ): Promise<DeviceTokenResponse> {
    let elapsed = 0;
    while (true) {
      const token = await this.pollForToken();
      if (token) return token;

      // Wait before next poll
      await new Promise((resolve) => setTimeout(resolve, POLLING_INTERVAL));
      elapsed += POLLING_INTERVAL / 1000;
      onProgress?.(elapsed);
    }
  }

  /**
   * Refresh the access token using the refresh token
   * Returns the new token response or throws on error
   */
  static async refreshToken(): Promise<DeviceTokenResponse> {
    const response = await fetch(`/api/auth/copilot/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include", // Important for cookies
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Token refresh failed: ${response.status} - ${error}`);
    }

    const data = await response.json();

    if (data.status === "success") {
      return {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        token_type: "bearer",
      };
    }

    throw new Error(`Token refresh error: ${data.error || "Unknown error"}`);
  }

  /**
   * Send chat completion request to Copilot API
   */
  async chatCompletion(
    request: ChatCompletionRequest,
  ): Promise<ChatCompletionResponse> {
    const response = await fetch(`/api/chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include", // Important for cookies
      body: JSON.stringify({
        provider: "copilot",
        ...request,
      }),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Copilot API error: ${response.status} - ${error}`);
    }

    return await response.json();
  }

  /**
   * Generate story elements
   */
  async generateStory(
    request: GenerateStoryRequest,
  ): Promise<GenerateStoryResponse> {
    const systemPrompt = `You are a creative storyteller for children aged ${
      request.context.ageRange || "6-12"
    }. Generate story elements in ${request.context.language}. Return valid JSON only.`;

    const userPrompt = this.buildStoryPrompt(request);

    const completion = await this.chatCompletion({
      model: PROVIDERS.copilot.defaultModel,
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt },
      ],
      temperature: 0.9,
      max_tokens: 1000,
    });

    const content = completion.choices[0]?.message?.content || "{}";
    return JSON.parse(content);
  }

  /**
   * Test connection to Copilot API
   */
  async testConnection(): Promise<boolean> {
    try {
      await this.chatCompletion({
        model: PROVIDERS.copilot.defaultModel,
        messages: [{ role: "user", content: "Hello" }],
        max_tokens: 5,
      });
      return true;
    } catch {
      return false;
    }
  }

  private buildStoryPrompt(request: GenerateStoryRequest): string {
    const { element } = request;
    if (element === "full_story") {
      return "Generate a complete short story with character, location, quest, and skills.";
    }
    return `Generate a ${element} for a children's story. Return JSON: { "elements": [{ "type": "${element}", "name": "...", "description": "...", "attributes": {...} }] }`;
  }
}
